import type {
  AlgorithmJoinsAndMappingsGraphNodeOfType,
  AlgorithmJoinsAndMappingsGraphNodeType,
} from './types';

// Resolve the owner join node for a DIMENSION_MAPPING/VALUE_MAPPING edge.
// If the edge carries a persisted joinNodeId and that node is still a valid owner, use it.
// Otherwise, fall back to the last algorithmSource node that qualifies as a valid owner.
export const resolveOwnerNode = ({
  edgeSourceNode,
  originalOwnerNode,
  allAlgorithmSourceNodes,
}: {
  edgeSourceNode: AlgorithmJoinsAndMappingsGraphNodeType;
  originalOwnerNode: AlgorithmJoinsAndMappingsGraphNodeType | undefined;
  allAlgorithmSourceNodes: AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>[];
}) => {
  if (
    originalOwnerNode &&
    originalOwnerNode?.type === 'algorithmSource' &&
    isValidAsOwner({
      potentialOwnerNode: originalOwnerNode,
      allAlgorithmSourceNodes,
      edgeSourceNode,
    })
  ) {
    return originalOwnerNode;
  }
  return [...allAlgorithmSourceNodes].reverse().find((potentialOwnerNode) =>
    isValidAsOwner({
      potentialOwnerNode,
      allAlgorithmSourceNodes,
      edgeSourceNode,
    }),
  );
};

// Find a valid owner node (join) for a DIMENSION_MAPPING/VALUE_MAPPING edge (join item)
// It must satisfy two conditions:
// 1. It must not be the first (root) join — the root join never has join items.
// 2. For DIMENSION_MAPPING join item, it must appear in a join that comes after the source join.
//    For VALUE_MAPPING join item this is not required — any non-root algorithmSource can own the item.
export const isValidAsOwner = ({
  potentialOwnerNode,
  allAlgorithmSourceNodes,
  edgeSourceNode,
}: {
  potentialOwnerNode: AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>;
  allAlgorithmSourceNodes: AlgorithmJoinsAndMappingsGraphNodeOfType<'algorithmSource'>[];
  edgeSourceNode: AlgorithmJoinsAndMappingsGraphNodeType;
}) =>
  allAlgorithmSourceNodes.indexOf(potentialOwnerNode) > 0 &&
  (edgeSourceNode.type !== 'algorithmSource' ||
    allAlgorithmSourceNodes.indexOf(edgeSourceNode) <
      allAlgorithmSourceNodes.indexOf(potentialOwnerNode));
